import { css, html, render } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { BaseVisualization, VisualizationOptions } from "./baseVisualization";
import { DashboardEvent, Dashboard as OGDashboard, DashboardItemModel, DashboardModel } from '@omegagrid/dashboard';
import { DashboardAdapter, DashboardItemSource, DashboardOptions } from "./dashboardAdapter";
import { ComponentFactory, ComponentId, MenuEvent, dom, actions } from "@omegagrid/core";
import { DashboardVisualizationItem } from "./dashboardItem";
import { ReportChangeEvent, ReportStore } from "../../model/reportStore";
import { UidMultiContextDataItem, createDashboardItemComponentId } from "../../model/itemUid";
import { cloneContext } from "../../model/cloneContext";
import { ContextData } from "@reporting/rp-types/analytics";

@customElement('rp-visual-dashboard')
export class DashboardVisualization extends BaseVisualization {

	static styles = [BaseVisualization.styles, css`
		:host {
			width: 100%;
			height: 100%;
			position: relative;
		}

		og-dashboard {
			height: 100%;
		}
	`];

	@property()
	highlightItemId: ComponentId;

	@property({type: Object})
	adapter: DashboardAdapter;

	@property()
	createComponent: ComponentFactory;

	@query('og-dashboard')
	dashboard: OGDashboard;

	private _items = new Map<ComponentId, DashboardVisualizationItem>();
	get items() { return this._items }

	private _itemSources: DashboardItemSource[];
	get itemSources() { return this._itemSources }

	private _itemSourceMap: Map<ComponentId, DashboardItemSource> = new Map();
	get itemSourceMap() {
		if (this._itemSourceMap.size) return this._itemSourceMap;
		this._itemSources?.forEach(item => this._itemSourceMap.set(item.id, item));
		return this._itemSourceMap;
	}

	/** backing store of the dashboard context (null for derived, storeless dashboards) */
	get store(): ReportStore {
		return this.adapter?.store ?? null;
	}

	removeItem(id: ComponentId) {
		this.adapter.removeItem(id);
		if (!this.store) {
			// storeless (derived) dashboard: prune the local sources by hand
			this.itemSourceMap.delete(id);
			this._itemSources = this._itemSources.filter(i => i.id !== id);
			this._items.delete(id);
			this.updateDashboard();
		}
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	async refreshItem(id: ComponentId) {
		this._items.get(id)?.refresh();
	}

	/**
	 * A dashboard derived from a single context (e.g. a chart view split by the
	 * chart axis) stands for the visualizations of its items - they all render
	 * the context the editor works with. A store backed dashboard is a
	 * visualization of its own.
	 */
	getVisualizations(): BaseVisualization[] {
		if (this.store) return [this];
		return [...this._items.values()].map(item => item.visualization).filter(Boolean);
	}

	/**
	 * Derived (storeless) dashboards render one context in several items - an
	 * edit of that context has to reach all of them; store backed dashboards
	 * refresh their items from the store change events instead.
	 */
	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		if (!contextData || this.store) return;
		this.getVisualizations().forEach(visualization => visualization.refresh(contextData));
	}

	async duplicateItem(id: ComponentId) {
		// the duplicate gets its own uid, existing items keep their component
		// identity - no content shifts, no refresh of the other items needed
		this.adapter.duplicateItem(id);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	layout() {
		this.dashboard?.layout();
	}

	/**
	 * All refreshes of a store-backed dashboard are driven by store change
	 * events - no matter whether the mutation came from the overlay buttons,
	 * the editor, the sidebar filter or the container toolbar. Thanks to the
	 * structural sharing of store snapshots, changed items are detected by
	 * a plain reference comparison.
	 */
	private _onStoreChange = async (e: Event) => {
		const scope = (e as ReportChangeEvent).scope;
		this.contextData = this.adapter.contextData;

		switch (scope) {
			case 'structure':
				await this.loadData();
				this.pruneItems();
				this.fixCollisions(); // e.g. a new/duplicated item lands on an occupied position
				break;
			case 'options':
				// item visibility depends on view_options.validate_access
				this.syncItemSources();
				await this.updateDashboard();
				// items inherit view_options.item_style, re-render them
				this._items.forEach(item => item.requestUpdate());
				break;
			case 'content':
				this.syncItemSources();
				// in-place refresh: content edits do not change the report data
				this.refreshChangedItems(false);
				break;
			case 'filters':
				// recreate: the item has to re-query its report data
				this.refreshChangedItems(true);
				break;
			case 'geometry':
			case 'reset':
				// geometry is already reflected in the og model; reset reloads
				// the whole report
				this.syncRenderedItems();
				break;
		}
	}

	/** last rendered item snapshot per component id (for change detection) */
	private _renderedItems = new Map<ComponentId, UidMultiContextDataItem>();

	private syncRenderedItems() {
		this._renderedItems.clear();
		if (!this.store) return;
		((this.adapter?.contextData?.items ?? []) as UidMultiContextDataItem[]).forEach(item => {
			this._renderedItems.set(createDashboardItemComponentId(this.store.idContext, item), item);
		});
	}

	/**
	 * Refresh items whose definition changed since the last render. In-place
	 * (visualization.refresh) keeps the component and its state; recreate
	 * drops the component so it reloads including its report data.
	 */
	private refreshChangedItems(recreate: boolean) {
		if (!this.store) return;
		((this.adapter?.contextData?.items ?? []) as UidMultiContextDataItem[]).forEach(item => {
			const id = createDashboardItemComponentId(this.store.idContext, item);
			const previous = this._renderedItems.get(id);
			this._renderedItems.set(id, item);
			if (previous === item || previous === undefined) return;

			const component = this._items.get(id);
			if (!component) return;
			if (!recreate && component.visualization && item.context) {
				component.visualization.refresh(cloneContext(item.context));
			} else {
				component.refresh();
			}
		});
	}

	/** drop cached item components that are no longer part of the dashboard */
	private pruneItems() {
		const ids = new Set(this._itemSources?.map(item => item.id));
		[...this._items.keys()].forEach(id => {
			if (ids.has(id)) return;
			this._items.delete(id);
			// og-dashboard caches components forever; without this the removed
			// item component would be served again if the id ever came back
			this.dashboard?.components.remove(id);
		});
	}

	/** refresh item sources (titles, styles) without rebuilding the og model */
	private syncItemSources() {
		if (!this.adapter) return;
		this._itemSources = this.adapter.getItems() as DashboardItemSource[];
		this._itemSources.forEach((item, i) => item.index = i);
		this._itemSourceMap.clear();
		this._items.forEach((item, id) => {
			const source = this.itemSourceMap.get(id);
			if (source) item.item = source;
		});
	}

	async loadData() {
		if (!this.adapter) return;
		this._itemSources = await this.adapter.getItems();
		this._itemSources.forEach((item, i) => item.index = i);
		this._itemSourceMap.clear();

		// sync sources of the already created item components
		this._items.forEach((item, id) => {
			const source = this.itemSourceMap.get(id);
			if (source) item.item = source;
		});

		this.syncRenderedItems();
		this.updateDashboard();
	}

	fixCollisions() {
		if (!this.dashboard?.model) return;
		this.dashboard.fixCollisions();
		// write the fixed positions back to the context data
		this.contextData = this.adapter.updateContext(this.dashboard.model);
	}

	/** design mode overlay with the item title and controls (rendered inside og-dashboard-item) */
	renderItemOverlay = (div: HTMLDivElement, model: DashboardItemModel) => {
		render(html`
			<div style="height: 100%; box-sizing: border-box; padding: 5px;background-color: var(--og-accent-color-alpha-30)">
				<div style="display: flex; align-items: center; flex-direction: row; gap: 2px; background-color: var(--og-accent-color-alpha-80); border-radius: var(--og-base-radius)">
					<div style="flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; padding: 0 5px">${model.title}</div>

					<og-button
						style="flex: 0 0 20px"
						color="transparent"
						.icon="${{icon: 'copy'}}"
						.title="${msg('Duplicate')}"
						@mousedown="${(e: MouseEvent) => e.stopPropagation()}"
						@click="${() => this.duplicateItem(model.id)}">
					</og-button>

					<og-button
						style="flex: 0 0 20px"
						color="transparent"
						.icon="${{icon: 'trash', color: 'red'}}"
						.title="${msg('Remove')}"
						@mousedown="${(e: MouseEvent) => e.stopPropagation()}"
						@click="${() => this.removeItem(model.id)}">
					</og-button>
				</div>
			</div>
		`, div);
	}

	async updateDashboard() {
		if (!this.dashboard) return;
		const options = await this.adapter.getOptions();
		options.overlay = {opacity: 0.3, renderer: this.renderItemOverlay};
		this.dashboard.model = new DashboardModel({
			items: this._itemSources,
			options: options
		});
	}

	async updateOptions(options: DashboardOptions) {
		// the store 'options' change event drives the dashboard/items refresh
		this.adapter.updateOptions(options);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** apply a recipe to the dashboard view_options (the 'options' change event refreshes the UI) */
	async updateViewOptions(recipe: (viewOptions: Record<string, any>) => void) {
		this.store?.updateViewOptions(recipe);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** apply a recipe to the item's context data (the 'content' change event syncs the sources) */
	async updateItem(id: ComponentId, recipe: (item: UidMultiContextDataItem) => void) {
		this.adapter?.updateItem(id, recipe);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** update the dashboard visualization for a new/changed dashboard view (rows, columns, ...) */
	async updated(props: Map<string, any>) {
		super.updated(props);
		if (props.has('adapter')) {
			const previous = props.get('adapter') as DashboardAdapter;
			previous?.store?.removeEventListener('change', this._onStoreChange);
			this.store?.addEventListener('change', this._onStoreChange);
			await this.loadData();
		}
		if (props.has('highlightItemId')) {
			this.items.forEach(item => item.highlight = (item.componentId === this.highlightItemId));
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.store?.removeEventListener('change', this._onStoreChange);
	}

	connectedCallback() {
		super.connectedCallback();
		// re-attached within the layout: the listener registration is idempotent
		this.store?.addEventListener('change', this._onStoreChange);
	}

	_createComponent = (id: ComponentId) => {
		const item = dom.createElement<DashboardVisualizationItem>('rp-visual-dashboard-item', {
			componentId: id,
			caption: this.itemSourceMap.get(id)?.title,
			createComponent: () => this.createComponent(id),
			dashboard: this,
			item: this.itemSourceMap.get(id),
			highlight: id == this.highlightItemId
		});
		item.addEventListener('menu.select', (e: MenuEvent) => {
			switch (e.item.k) {
				case 'split':
					// the store 'structure' change event reloads the dashboard data
					this.adapter.split(
						this.dashboard.model.items.find(i => i.id === id),
						item.visualization.itemCount
					);
					break;
			}
		});

		this.items.set(id, item);
		return item;
	}

	edit() {
		this.dashboard.designMode = !this.dashboard.designMode;
	}

	_onMove = (e: DashboardEvent) => {
		this.contextData = this.adapter.updateContext(this.dashboard.model);
		this.dispatchEvent(new CustomEvent('edit', {detail: e, bubbles: true, composed: true}));
	}
	
	_onResize = (e: DashboardEvent) => {
		this.contextData = this.adapter.updateContext(this.dashboard.model);
		this.dashboard.components.forEach(c => c.layout());
		this.dispatchEvent(new CustomEvent('edit', {detail: e, bubbles: true, composed: true}));
	}

	_onAction = (e: actions.ActionEvent) => {
		const item = e.action.value as DashboardItemSource;
		
		if (e.action.key == 'remove') {
			this.removeItem(item.id);
			return;
		}

		this.adapter?.onAction(e.action.key, item);
	}

	render = () => html`
		<og-dashboard
			@dashboard.action="${this._onAction}"
			@dashboard.movestop="${this._onMove}"
			@dashboard.resizestop="${this._onResize}"
			.createComponent="${this._createComponent}">
		</og-dashboard>
	`;

	getOptions(): VisualizationOptions {
		return this.createOptions({
			edit: true,
			addMenu: [
				{id: 'savedView', text: msg('Add saved view'), icon: 'bookmark'},
				{id: 'text', text: msg('Add text'), icon: 'text'},
				{id: 'links', text: msg('Add links'), icon: 'link'},
			]
		});
	}

}
